import { is, List, Record } from 'immutable';
import {
  BEGIN_SAVE,
  SAVED_TO_SERVER,
  SAVE_FAILED,
  UPDATE_FROM_SERVER,
  DISCARD_LOCAL_CHANGES,
} from 'app/remote/generic/actions';
import { BASE_ACTION, ENTITY_PREFIX, LOG_PREFIX } from 'app/remote/generic/constants';

export const Log = Record({
  inflight: List(),
  local:    List(),
  version:  0,
});

const baseAction = (action) => action.meta && action.meta[BASE_ACTION];
const optimisticAction = (action) => action.meta && action.meta.optimistic;

// The log reducer has to know the difference between an action that's being
// replayed and an action that's new and should be recorded. So while we export
// an interface for combineReducers, we integrate the log updates inside the
// reconciler.

// Placeholder for combineReducers, the actual log reducer is nested inside the
// reconcile step.
export const logReducer = (state = Log(), action) => state;

const makeLogReducer = (dependencies) => (state = Log(), action) => {
  switch (action.type) {
    case BEGIN_SAVE: {
      // seperate saved from unsaved
      // must not be already inflight
      return Log({
        version:  state.version,
        local:    List(),
        inflight: state.local,
      });
    }

    case SAVED_TO_SERVER: {
      // discard all actions that were saved
      return state.set('inflight', List());
    }

    case SAVE_FAILED: {
      // nothing was persisted, entire transaction rolled back, bail bail bail
      return Log({
        version:  state.version,
        local:    state.inflight.concat(state.local),
        inflight: List(),
      });
    }

    case DISCARD_LOCAL_CHANGES: {
      return Log();
    }

    default:
      if (optimisticAction(action)) {
        return state
          .update('local', (local) => local.push(action))
          .set('version', state.version + 1);
      } else {
        return state;
      }
  }
};

export const makeReconcileReducer = (dependencies, inner) => {
  const innerLogReducer = makeLogReducer(dependencies);
  const logMountPoint   = [dependencies.prefix, LOG_PREFIX];

  return (state, action) => {
    let newState = state;

    const oldLog = newState.getIn(logMountPoint);
    const newLog = innerLogReducer(oldLog, action);
    if (newLog !== oldLog) {
      newState = newState.setIn(logMountPoint, newLog);
    }

    if (action.type === UPDATE_FROM_SERVER || action.type === SAVED_TO_SERVER) {
      const log = newState.getIn(logMountPoint);

      if (!log.inflight.isEmpty()) {
        // If we have inflight changes, play those into the state before considering local changes
        newState = log.inflight.reduce(inner, newState);
      }

      // rerun all clobbered actions -- this must be run after the entity update
      newState = log.local.reduce(inner, newState);
    }

    return newState;
  };
};

// Creates a selector for seeing if there are any edits.
export const makeHasEdits = (dependencies) => {
  const { getLocalMap, prefix } = dependencies;

  /**
   * @param {Map}      state
   * @param {function} typeObject - Record factory for an entity type.
   * @param {string}   typeObject.storePrefix
   * @param {string}   id
   * @param {string}   field
   * @returns {boolean}
   */
  return (state, typeObject, id, field) => {
    const relativeLookup = [typeObject.storePrefix, id, field];
    const local = getLocalMap(state).getIn(relativeLookup);
    const remote = state.getIn([prefix, ENTITY_PREFIX, ...relativeLookup]);
    return !is(local, remote);
  };
};

// Are there changes we haven't persisted fully?
export const makeHasUnsavedChanges = (dependencies) => {
  const logMountPoint = [dependencies.prefix, LOG_PREFIX];

  // Remove base edits from local (since they aren't considered actions performed)
  // by the user.
  const filterBaseActions = (action) => !baseAction(action);

  return (state) => {
    const log = state.getIn(logMountPoint);
    return !log.inflight.isEmpty() || !log.local.filter(filterBaseActions).isEmpty();
  };
};

export const makeLogVersion = (dependencies) => {
  const logMountPoint = [dependencies.prefix, LOG_PREFIX];
  return (state) => {
    const log = state.getIn(logMountPoint);
    return log.version;
  };
};
